import { useEffect, useRef, useState } from 'react';
import * as Blockly from 'blockly';
import { registerAllBlocks, buildToolbox, isWebSerialSupported, getBlockMeta } from '@spblock/core';
import { ebsTheme } from '../theme/blockly-theme';
import { setWorkspace, getWorkspace } from '../lib/workspace';
import { markCloudDirty } from '../lib/autosave';
import { useStore } from '../state/store';
import { IconZoomIn, IconZoomOut, IconZoomFit } from './icons';

interface BlockInfo {
  label: string;
  description: string;
}

function describeBlock(ws: Blockly.WorkspaceSvg, id: string): BlockInfo | null {
  let block: Blockly.Block | null = ws.getBlockById(id) ?? null;
  if (!block) {
    const flyoutWorkspace = ws.getFlyout()?.getWorkspace();
    block = (flyoutWorkspace ? flyoutWorkspace.getBlockById(id) : null) ?? null;
  }
  if (!block) return null;
  if (block.isShadow()) {
    // Explain the real block the shadow belongs to, not the placeholder value.
    block = block.getSurroundParent() ?? block;
  }
  const meta = getBlockMeta(block.type);
  if (meta) {
    return { label: meta.label, description: meta.description };
  }
  const tooltip = typeof block.tooltip === 'string' ? block.tooltip : '';
  if (tooltip) {
    const label = block.type.replace(/^esp32_/, '').replace(/_/g, ' ');
    return { label: label.charAt(0).toUpperCase() + label.slice(1), description: tooltip };
  }
  return null;
}

let autosaveTimer: number | undefined;

export function WorkspaceCanvas() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [serialSupported] = useState(isWebSerialSupported());
  const [blockInfo, setBlockInfo] = useState<BlockInfo | null>(null);

  useEffect(() => {
    registerAllBlocks();
    const host = containerRef.current;
    if (!host) return;

    const toolboxDef = {
      kind: 'categoryToolbox',
      contents: buildToolbox(),
    } as unknown as Blockly.utils.toolbox.ToolboxDefinition;

    const ws = Blockly.inject(host, {
      toolbox: toolboxDef,
      renderer: 'zelos',
      theme: ebsTheme,
      sounds: false,
      trashcan: false,
      grid: { spacing: 26, length: 3, colour: '#E0E0E0', snap: true },
      zoom: {
        controls: false,
        wheel: true,
        pinch: true,
        startScale: 0.9,
        minScale: 0.4,
        maxScale: 2.2,
      },
      move: {
        scrollbars: { horizontal: true, vertical: true },
        drag: true,
        wheel: true,
      },
    }) as Blockly.WorkspaceSvg;
    setWorkspace(ws);

    // A project handed over from the home page (or a fresh canvas) wins over
    // the autosaved copy; otherwise restore the autosave.
    const pending = useStore.getState().pendingProject;
    if (pending) {
      useStore.getState().setPendingProject(null);
      if (pending.data) {
        try {
          Blockly.serialization.workspaces.load(JSON.parse(pending.data) as never, ws);
        } catch {
          useStore.getState().pushLine('err', 'That project could not be loaded onto the canvas.');
        }
      } else {
        ws.clear();
        localStorage.removeItem('spblock.workspace');
      }
    } else {
      const saved = localStorage.getItem('spblock.workspace');
      if (saved) {
        try {
          Blockly.serialization.workspaces.load(JSON.parse(saved) as never, ws);
        } catch {
          localStorage.removeItem('spblock.workspace');
        }
      }
    }

    const scheduleAutosave = () => {
      window.clearTimeout(autosaveTimer);
      autosaveTimer = window.setTimeout(() => {
        try {
          localStorage.setItem(
            'spblock.workspace',
            JSON.stringify(Blockly.serialization.workspaces.save(ws)),
          );
        } catch {
          /* storage full — autosave is best-effort */
        }
      }, 800);
    };
    ws.addChangeListener((e) => {
      if (e.isUiEvent) return;
      scheduleAutosave();
      markCloudDirty();
    });

    const observer = new ResizeObserver(() => Blockly.svgResize(ws));
    observer.observe(host);

    // Hover explanations: resolve the hovered block (canvas or flyout) through
    // the registry and surface its label + description in the info bar.
    const onMouseOver = (e: MouseEvent): void => {
      const el = (e.target as Element | null)?.closest?.('[data-id]');
      const id = el?.getAttribute('data-id');
      if (!id) {
        setBlockInfo(null);
        return;
      }
      setBlockInfo(describeBlock(ws, id));
    };
    const onMouseLeave = (): void => setBlockInfo(null);
    host.addEventListener('mouseover', onMouseOver);
    host.addEventListener('mouseleave', onMouseLeave);

    return () => {
      observer.disconnect();
      host.removeEventListener('mouseover', onMouseOver);
      host.removeEventListener('mouseleave', onMouseLeave);
      window.clearTimeout(autosaveTimer);
      setWorkspace(null);
      ws.dispose();
    };
  }, []);

  function withWs(fn: (ws: Blockly.WorkspaceSvg) => void): void {
    const ws = getWorkspace();
    if (ws) fn(ws);
  }

  return (
    <div className="canvas-area">
      <div
        className={`canvas-infobar ${blockInfo ? '' : 'is-hint'}`}
        role="status"
        aria-live="polite"
      >
        {blockInfo ? (
          <>
            <span className="infobar-label">{blockInfo.label}</span>
            <span className="infobar-desc">{blockInfo.description}</span>
          </>
        ) : (
          <>
            <span className="infobar-label">Tip</span>
            <span className="infobar-desc">Hover any block — in the toolbox or on the canvas — to see what it does.</span>
          </>
        )}
      </div>

      <div
        className={`canvas-capability ${serialSupported ? '' : 'is-warn'}`}
        title={
          serialSupported
            ? 'Web Serial is available — boards can be flashed directly from this browser.'
            : 'Web Serial is not available. Programs can be built and exported, but flashing requires Chrome or Edge.'
        }
      >
        <span className="dot" />
        {serialSupported ? 'Web Serial ready' : 'Flashing unavailable — use Chrome or Edge'}
      </div>

      <div ref={containerRef} className="blockly-container" />

      <div className="zoom-controls">
        <button title="Zoom in" aria-label="Zoom in" onClick={() => withWs((ws) => ws.zoomCenter(1))}>
          <IconZoomIn />
        </button>
        <button title="Zoom out" aria-label="Zoom out" onClick={() => withWs((ws) => ws.zoomCenter(-1))}>
          <IconZoomOut />
        </button>
        <button title="Fit blocks to view" aria-label="Fit blocks to view" onClick={() => withWs((ws) => ws.zoomToFit())}>
          <IconZoomFit />
        </button>
      </div>
    </div>
  );
}
